/**
 * Slugs da URL (§5.2.1): segmentos em pt-BR, sempre minúsculos, e as listas fechadas contra as quais todo valor da URL
 * é comparado (§5.2.10). O roteador importa só tipos de `contracts/` (FE §4.2), por isso as 27 UFs ficam aqui.
 */
import type { UfCode } from '../contracts/base';
import type { MapMode, RouteOffice } from '../model/types';

/** `presidente ↔ president`, `governadores ↔ governor`, `senado ↔ senate`, `deputados ↔ chamber`. */
export const OFFICE_SLUGS: Readonly<Record<RouteOffice, string>> = Object.freeze({
  president: 'presidente',
  governor: 'governadores',
  senate: 'senado',
  chamber: 'deputados',
});

/** `municipios ↔ municipalities`, `estados ↔ states`, `vantagem ↔ advantage`, `apurado ↔ counted`, `candidato ↔ candidate`. */
export const MODE_SLUGS: Readonly<Record<MapMode, string>> = Object.freeze({
  municipalities: 'municipios',
  states: 'estados',
  advantage: 'vantagem',
  counted: 'apurado',
  candidate: 'candidato',
});

function invert<K extends string>(o: Readonly<Record<K, string>>): ReadonlyMap<string, K> {
  return new Map(Object.entries(o).map(([k, v]) => [v as string, k as K]));
}

export const SLUG_TO_OFFICE: ReadonlyMap<string, RouteOffice> = invert(OFFICE_SLUGS);
export const SLUG_TO_MODE: ReadonlyMap<string, MapMode> = invert(MODE_SLUGS);

/** Segmento do Exterior. */
export const ABROAD_SLUG = 'exterior';

/** A tela de comentários no hash: `?tela=comentarios` (ORD-F-026; docs/TELA-COMENTARIOS.md §1). */
export const SCREEN_PARAM = 'tela';
export const COMMENTS_SLUG = 'comentarios';

/** As 27 UFs (nunca "ZZ"), na forma da memória (maiúsculas). */
export const UF_LIST: readonly UfCode[] = Object.freeze([
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
] as const);

const UF_SET: ReadonlySet<string> = new Set(UF_LIST);

/** Segmento da URL ("sp") → UF ("SP"), ou null. "zz" nunca é UF (§5.2.1). */
export function ufFromSlug(seg: string): UfCode | null {
  if (!/^[a-z]{2}$/.test(seg)) return null;
  const uf = seg.toUpperCase();
  return UF_SET.has(uf) ? (uf as UfCode) : null;
}

/**
 * Os dois primeiros dígitos do código IBGE do município são o código IBGE da UF: inferência pela forma enquanto a
 * Geography não chegou (§5.2.9).
 */
const IBGE_UF_PREFIX: Readonly<Record<string, UfCode>> = Object.freeze({
  '11': 'RO',
  '12': 'AC',
  '13': 'AM',
  '14': 'RR',
  '15': 'PA',
  '16': 'AP',
  '17': 'TO',
  '21': 'MA',
  '22': 'PI',
  '23': 'CE',
  '24': 'RN',
  '25': 'PB',
  '26': 'PE',
  '27': 'AL',
  '28': 'SE',
  '29': 'BA',
  '31': 'MG',
  '32': 'ES',
  '33': 'RJ',
  '35': 'SP',
  '41': 'PR',
  '42': 'SC',
  '43': 'RS',
  '50': 'MS',
  '51': 'MT',
  '52': 'GO',
  '53': 'DF',
});

/** UF do município pela forma do código IBGE (7 dígitos), ou null. */
export function ufFromIbge(ibge: string): UfCode | null {
  return /^\d{7}$/.test(ibge) ? (IBGE_UF_PREFIX[ibge.slice(0, 2)] ?? null) : null;
}

export const IBGE_RE = /^\d{7}$/;
export const CITY_RE = /^\d{5}$/;
export const ISO2_RE = /^[a-z]{2}$/;
export const ZONE_RE = /^z(\d{1,4})$/;
export const CAND_RE = /^\d{2}$/;
export const HHMM_RE = /^(\d{2})(\d{2})$/;
